import React, { useCallback, useMemo } from 'react';

import { List } from '@fastlessjs/backoffice-layer/dist/components/templates';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { pick } from 'lodash';

import { OrderReturnExtra } from './OrderReturnExtra';
import { Return, ReturnInfos, ReturnLine } from '../../returns/types';
import { buildReturnPayloadForOrder } from '../../returns/utils/buildReturnPayload';
import { ReturnRequestTable } from '../../returns/views/ReturnRequestTable';
import { orderListSchema } from '../schemas';
import { OrderLine, ReturnOrder } from '../types';

// Register this component for debug tracking
debug.registerComponent('OrdersReturnRequestTable');

type OrdersReturnRequestTableProps = {
  onEdit: (orderId: string, clientId?: string) => void;
  ExtraButton?: (props?: any) => JSX.Element;
  mutateAsync: (payload: Partial<Return>) => Promise<unknown>;
  isSubmitLocked: boolean;
  returnOrderLines: Record<string, Record<string, ReturnLine & OrderLine & ReturnInfos>>;
};

const OrdersReturnRequestTable = (props: OrdersReturnRequestTableProps) => {
  debug.trackRender('OrdersReturnRequestTable');
  const { onEdit, ExtraButton, mutateAsync, isSubmitLocked, returnOrderLines } = props;
  debug.log(
    'OrdersReturnRequestTable',
    `Rendering with ${Object.keys(returnOrderLines).length} return orders`,
  );

  const returnOrderData = useMemo(() => {
    return Object.keys(returnOrderLines).map((key) => ({
      refc: key.split('/')[0],
      ndoc: key.split('/')[1],
      lignes: Object.values(returnOrderLines[key]),
    }));
  }, [returnOrderLines]);

  const handleAskReturn = useCallback(
    async (orderId: string, clientId?: string) => {
      if (isSubmitLocked) {
        return;
      }

      debug.log(
        'OrdersReturnRequestTable',
        `Submitting return for order: ${orderId}, client: ${clientId}`,
      );

      const payload = buildReturnPayloadForOrder(returnOrderLines, clientId, orderId);
      if (!payload) {
        return;
      }

      try {
        await mutateAsync(payload);
      } catch (error) {
        debug.error('OrdersReturnRequestTable', `Return submission failed: ${error}`);
      }
    },
    [isSubmitLocked, mutateAsync, returnOrderLines],
  );

  return (
    <div style={{ paddingTop: '24px' }}>
      <List<ReturnOrder>
        hasHeader={false}
        enableSelection={false}
        data={returnOrderData}
        org=""
        schema={pick(orderListSchema, [
          'properties.ndoc',
          'properties.refc',
          'properties.lignes',
        ])}
        idKey="ndoc"
        rowKey={(record) => `${record?.ndoc}_${record?.refc}`}
        ExtraActions={(record: any) =>
          OrderReturnExtra({
            ...record,
            onEdit,
            onSubmit: handleAskReturn,
            isSubmitLocked,
          })
        }
        // eslint-disable-next-line react/no-unstable-nested-components
        ExpandableInfos={(record: ReturnOrder) => {
          return (
            <div
              key={`${record?.refc}_${record?.ndoc}`}
              className="expandable-container"
              style={{ padding: '24px 18px 0 0' }}
            >
              <ReturnRequestTable
                hasTitle={false}
                hasHeader={false}
                hasButton={false}
                hasBorder={false}
                clientId={record?.refc}
                orderId={record?.ndoc}
                key={`${record?.ndoc}_${record?.refc}`}
              />
            </div>
          );
        }}
      />
      {ExtraButton && (
        <div className="full-right">
          <ExtraButton />
        </div>
      )}
    </div>
  );
};

export { OrdersReturnRequestTable };

// Mark this component as initialized
debug.markInitialized('OrdersReturnRequestTable');
